import { useTranslation } from "react-i18next";
import { Stack, Box, Typography, IconButton, Tooltip, Button, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import CheckIcon from "@mui/icons-material/Check";
import DriveFileRenameOutlineRoundedIcon from "@mui/icons-material/DriveFileRenameOutlineRounded";
import WidgetsRoundedIcon from "@mui/icons-material/WidgetsRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import { useDrag, useDrop } from "react-dnd";
import { FieldIcon } from "../../../Field/FieldIcon";
import { getTypeText, FieldType } from "../../../configs";
// import { FieldBody } from "../FieldForm";
import { useState } from "react";
import { ContentModelField, RepeaterSubField } from "shell/services/types";
type SubFieldProps = {
  field: RepeaterSubField;
  parentName: string;
  onRemoveField: () => void;
  onMoveField: (draggedField: RepeaterSubField, dropIndex: number) => void;
  onDropField: () => void;
  onEditField: (field: RepeaterSubField) => void;
  index: number;
};
export const SubField = ({
  field,
  parentName,
  onRemoveField,
  onMoveField,
  onDropField,
  index,
  onEditField
}: SubFieldProps) => {
  const {
    t
  } = useTranslation();
  const TYPE_TEXT = getTypeText(t);
  const [isFieldLabelCopied, setIsFieldLabelCopied] = useState(false);
  const [isZuidCopied, setIsZuidCopied] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleCopy = async (e: React.MouseEvent, text: string, setCopied: React.Dispatch<React.SetStateAction<boolean>>) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy", error);
    }
  };
  const handleMenuClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleOpenEditModalField = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAnchorEl(null);
    onEditField(field);
  };
  const [{
    isDragging
  }, drag, preview] = useDrag({
    type: "relationalItem",
    item: {
      field,
      index
    },
    collect: monitor => ({
      isDragging: monitor.isDragging()
    })
  });
  const [_, drop] = useDrop({
    accept: "relationalItem",
    hover: ({
      field: draggedField,
      index: draggedIndex
    }) => {
      if (draggedIndex !== index) {
        onMoveField(draggedField, index);
      }
    },
    drop: () => {
      onDropField();
    }
  });
  return <Stack data-cy={`SubField_${field.name}`} ref={node => drop(preview(node))} draggable direction="row" minHeight="40px" border="1px solid" borderColor="border" borderRadius={2} bgcolor="common.white" alignItems="center" justifyContent="space-between" pr={1} pl={0.5} gap={1} sx={{
    opacity: isDragging ? 0.5 : 1,
    "&:hover": {
      backgroundColor: "action.hover",
      cursor: "pointer"
    }
  }} onClick={handleOpenEditModalField} data-sentry-element="Stack" data-sentry-component="SubField" data-sentry-source-file="SubField.tsx">
      <Box display="grid" gridTemplateColumns="28px 24px minmax(auto, min-content) 130px" alignItems="center" data-sentry-element="Box" data-sentry-source-file="SubField.tsx">
        <IconButton ref={drag} className="drag-handle" size="small" disableRipple sx={{
        cursor: "grab",
        mr: 0.5
      }} onClick={e => e.stopPropagation()} data-sentry-element="IconButton" data-sentry-source-file="SubField.tsx">
          <DragIndicatorRoundedIcon sx={{
          width: "20px",
          height: "20px"
        }} data-sentry-element="DragIndicatorRoundedIcon" data-sentry-source-file="SubField.tsx" />
        </IconButton>
        <FieldIcon type={field.datatype} data-sentry-element="FieldIcon" data-sentry-source-file="SubField.tsx" />
        <Tooltip title={field.label} enterDelay={3000} data-sentry-element="Tooltip" data-sentry-source-file="SubField.tsx">
          <Typography px={1.5} variant="body2" fontWeight="700" noWrap data-sentry-element="Typography" data-sentry-source-file="SubField.tsx">
            {field.label} {"required" in field && field.required && "*"}
          </Typography>
        </Tooltip>
        <Typography variant="body3" color="text.secondary" data-sentry-element="Typography" data-sentry-source-file="SubField.tsx">
          {TYPE_TEXT[field.datatype as FieldType]}
        </Typography>
      </Box>

      <Stack direction="row" alignItems="center" maxWidth="180px" data-sentry-element="Stack" data-sentry-source-file="SubField.tsx">
        <Button size="small" variant="contained" color="inherit" startIcon={isFieldLabelCopied && <CheckIcon />} sx={{
        "&:hover": {
          bgcolor: "grey.200"
        }
      }} onClick={e => handleCopy(e, `${parentName}.${field.name}`, setIsFieldLabelCopied)} data-sentry-element="Button" data-sentry-source-file="SubField.tsx">
          <Typography component="span" variant="caption" noWrap fontFamily="Roboto Mono" data-sentry-element="Typography" data-sentry-source-file="SubField.tsx">
            {isFieldLabelCopied ? t("common.copied") : `${parentName}.${field.name}`}
          </Typography>
        </Button>
        <IconButton data-cy={`OpenFieldDropdown_${field.name}`} onClick={handleMenuClick} size="small" sx={{
        ml: 1
      }} data-sentry-element="IconButton" data-sentry-source-file="SubField.tsx">
          <MoreHorizRoundedIcon sx={{
          width: "20px",
          height: "20px"
        }} data-sentry-element="MoreHorizRoundedIcon" data-sentry-source-file="SubField.tsx" />
        </IconButton>
        <Menu open={!!anchorEl} onClose={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setAnchorEl(null);
      }} anchorEl={anchorEl} anchorOrigin={{
        vertical: "bottom",
        horizontal: "right"
      }} transformOrigin={{
        vertical: "top",
        horizontal: "right"
      }} data-sentry-element="Menu" data-sentry-source-file="SubField.tsx">
          <MenuItem data-cy={`DropdownEditField_${field.name}`} onClick={handleOpenEditModalField} data-sentry-element="MenuItem" data-sentry-source-file="SubField.tsx">
            <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="SubField.tsx">
              <DriveFileRenameOutlineRoundedIcon data-sentry-element="DriveFileRenameOutlineRoundedIcon" data-sentry-source-file="SubField.tsx" />
            </ListItemIcon>
            <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="SubField.tsx">{t("schema.editField")}</ListItemText>
          </MenuItem>
          <MenuItem onClick={e => handleCopy(e, (field as ContentModelField).ZUID, setIsZuidCopied)} data-sentry-element="MenuItem" data-sentry-source-file="SubField.tsx">
            <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="SubField.tsx">
              {isZuidCopied ? <CheckIcon /> : <WidgetsRoundedIcon />}
            </ListItemIcon>
            <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="SubField.tsx">
              {isZuidCopied ? t("common.copied") : t("schema.copyFieldZUID")}
            </ListItemText>
          </MenuItem>

          <MenuItem data-cy={`DeactivateReactivateFieldDropdown_${field.name}`} onClick={e => {
          e.stopPropagation();
          onRemoveField();
        }} data-sentry-element="MenuItem" data-sentry-source-file="SubField.tsx">
            <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="SubField.tsx">
              <HighlightOffRoundedIcon data-sentry-element="HighlightOffRoundedIcon" data-sentry-source-file="SubField.tsx" />
            </ListItemIcon>
            <ListItemText data-sentry-element="ListItemText" data-sentry-source-file="SubField.tsx">{t("schema.removeField")}</ListItemText>
          </MenuItem>
        </Menu>
      </Stack>
    </Stack>;
};